/**
 * Role Permission Utilities
 * 
 * Simple helper functions for checking user permissions based on roles.
 * These use the Firebase-managed role configurations as the source of truth.
 * 
 * For React components, prefer using the usePermissions hook from @/hooks/useRoles
 * which provides dynamic permission checking based on Firebase role configurations.
 */

import { UserRole, ApprovedUser, Permission } from '@/types';

/**
 * Filter admin users from a list based on current user's role
 * Admin users cannot see other admin users (unless they have special permission)
 * 
 * @param users - Array of users to filter
 * @param currentUser - The current logged-in user
 * @returns Filtered array with admins removed if current user is admin
 */
export function filterAdminUsers(users: ApprovedUser[], currentUser?: ApprovedUser | null): ApprovedUser[] {
  if (!currentUser) return users;
  
  // If current user is an admin, filter out other admin users
  const isAdmin = currentUser.roles?.includes('admin');
  
  if (isAdmin) {
    return users.filter(user => {
      // Keep the current user in the list
      if (user.id === currentUser.id) return true;
      
      // Filter out other users who have admin role
      const hasAdminRole = user.roles?.includes('admin');
      return !hasAdminRole;
    });
  }
  
  // Non-admin users see all users
  return users;
}
